import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Modal, notification } from 'antd';
import moment from 'moment';
import APIClient from '../api/api';
import { Domain, DomainAuditEvent, DomainInfo } from '../api/models';
import { formatCurrency, formatCurrencyCents, formatPickSource, formatPickStatus } from '../utils';
import { Column, DataTable, DomainName, Pagination, StatusTag, TabItem, Tabs, TagTone } from './ui';
import TagSelectDomain from './TagsSelectDomain';
import DomainContactMigration from './DomainContactMigration';
import DomainFastTransfer from './DomainFastTransfer';
import AuditDataCell from './AuditDataCell';

const AUDIT_PAGE_SIZE = 10;

export interface Props {
  /** Domain name (e.g. "example.com"). */
  domain: string;
  onClose: () => void;
}

const pickStatusTone: Record<string, TagTone> = {
  pending: 'warn',
  upstreamPicked: 'success',
  autoPicked: 'success',
  userRenew: 'accent',
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; info: DomainInfo }
  | { kind: 'notFound' }
  | { kind: 'error' };

const DomainInfoModal = ({ domain, onClose }: Props) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const result = await APIClient.getDomain(domain);
        if (cancelled) return;
        if (result) setState({ kind: 'loaded', info: result });
        else setState({ kind: 'notFound' });
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          notification.error({ message: 'Failed to load domain info.' });
          setState({ kind: 'error' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain]);

  const info = state.kind === 'loaded' ? state.info : null;

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={960}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <ModalHeader domain={domain} info={info} onClose={onClose} />
      <div className="max-h-[70vh] overflow-auto">
        {state.kind === 'loading' && (
          <div className="px-6 py-12 text-center text-text-3 text-[12.5px]">Loading…</div>
        )}
        {state.kind === 'loaded' && <ModalBody info={state.info} />}
        {state.kind === 'notFound' && <NotFound domain={domain} />}
        {state.kind === 'error' && (
          <div className="px-6 py-12 text-center text-danger text-[12.5px]">
            Failed to load domain info.
          </div>
        )}
      </div>
    </Modal>
  );
};

function NotFound({ domain }: { domain: string }) {
  return (
    <div className="px-6 py-12 flex flex-col items-center gap-2 text-center">
      <div className="text-[28px] leading-none text-text-3">∅</div>
      <div className="text-[14px] font-semibold text-text">Domain not found</div>
      <div className="text-[12.5px] text-text-3 max-w-[440px]">
        <span className="font-mono text-text-2">{domain}</span> isn't in the system — not as a
        portfolio holding, expiring lead, sale, or anywhere else. It may have been removed, never
        imported, or the spelling is off.
      </div>
    </div>
  );
}

export default DomainInfoModal;

function ModalHeader({
  domain,
  info,
  onClose,
}: {
  domain: string;
  info: DomainInfo | null;
  onClose: () => void;
}) {
  const d = info?.domain;
  return (
    <div className="px-6 pt-5 pb-4 border-b border-border bg-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1">Domain</div>
          <div className="text-[22px] leading-none">
            <DomainName name={domain} />
          </div>
          {d && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {d.status && (
                <StatusTag tone={statusTone(d.status)}>{d.status}</StatusTag>
              )}
              {d.pickStatus && !(d.pickStatus === 'pending' && d.status?.toLowerCase().includes('active') && d.accountId) && (
                <StatusTag tone={pickStatusTone[d.pickStatus] ?? 'neutral'}>
                  {formatPickStatus(d.pickStatus)}
                </StatusTag>
              )}
              {d.autoRenew ? (
                <StatusTag tone="accent" dot={false}>auto-renew on</StatusTag>
              ) : (
                <StatusTag tone="neutral" dot={false}>auto-renew off</StatusTag>
              )}
              {d.whoisGuard ? (
                <StatusTag tone="success" dot={false}>whois guard</StatusTag>
              ) : (
                <StatusTag tone="neutral" dot={false}>no whois guard</StatusTag>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text flex-shrink-0"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

type TabKey = 'overview' | 'stats' | 'events' | 'contactMigration' | 'fastTransfer';

function ModalBody({ info }: { info: DomainInfo }) {
  const d = info.domain;
  const hasStatsTab = !!d.stats && hasStats(d.stats);
  const [tab, setTab] = useState<TabKey>('overview');

  const tabs: TabItem[] = [
    { key: 'overview', label: 'Overview' },
    ...(hasStatsTab ? [{ key: 'stats', label: 'Stats' } as TabItem] : []),
    { key: 'events', label: 'Audit', count: info.auditEvents.length },
    { key: 'contactMigration', label: 'Contact Migration' },
    { key: 'fastTransfer', label: 'Fast Transfer' },
  ];

  return (
    <div className="flex flex-col bg-bg">
      <div className="px-6 pt-4 pb-3 border-b border-border bg-panel">
        <Tabs items={tabs} activeKey={tab} onChange={(k) => setTab(k as TabKey)} />
      </div>
      <div className="px-6 py-5">
        {tab === 'overview' && <OverviewTab domain={d as Domain} />}
        {tab === 'stats' && hasStatsTab && <StatsTab stats={d.stats!} />}
        {tab === 'events' && <AuditEventsSection events={info.auditEvents} />}
        {tab === 'contactMigration' && <DomainContactMigration domain={d.domain} />}
        {tab === 'fastTransfer' && <DomainFastTransfer domain={d.domain} />}
      </div>
    </div>
  );
}

function OverviewTab({ domain: d }: { domain: Domain }) {
  return (
    <div className="flex flex-col gap-6">
      <Section title="Details">
        <MetaGrid>
          <Meta label="Account">{d.accountId || '—'}</Meta>
          <Meta label="Source">{formatPickSource(d.source) || '—'}</Meta>
          <Meta label="Added">
            {d.addedAt ? moment(d.addedAt).format('YYYY-MM-DD') : '—'}
          </Meta>
          <Meta label="Last sync">
            {d.lastSyncAt ? moment(d.lastSyncAt).format('YYYY-MM-DD HH:mm') : '—'}
          </Meta>
          <Meta label="Created">
            {d.createdAt ? moment(d.createdAt).utc().format('YYYY-MM-DD') : '—'}
          </Meta>
          <Meta label="Expires">
            {d.expiresAt ? (
              <span className="flex items-baseline gap-1.5">
                <span className="font-mono">{moment(d.expiresAt).utc().format('YYYY-MM-DD')}</span>
                <span className="text-text-3 text-[11px]">
                  {moment(d.expiresAt).utc().fromNow()}
                </span>
              </span>
            ) : (
              '—'
            )}
          </Meta>
          {d.pickedAt && (
            <Meta label="Picked">{moment(d.pickedAt).format('YYYY-MM-DD')}</Meta>
          )}
          <Meta label="Custom DNS">{d.customDNS ? 'yes' : 'no'}</Meta>
        </MetaGrid>
      </Section>

      {d.nameservers && d.nameservers.length > 0 && (
        <Section title="Nameservers">
          <div className="flex flex-wrap gap-1.5">
            {d.nameservers.map((ns) => (
              <span
                key={ns}
                className="font-mono text-[11.5px] text-text-2 bg-bg-elevated px-2 py-1 rounded"
              >
                {ns}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section title="Tags">
        <div className="max-w-[520px]">
          <TagSelectDomain domain={d} />
        </div>
      </Section>
    </div>
  );
}

function StatsTab({ stats }: { stats: NonNullable<Domain['stats']> }) {
  return (
    <Section title="Stats">
      <MetaGrid>
        {stats.appraisedValueCents != null && (
          <Meta label="Appraised">{formatCurrencyCents(stats.appraisedValueCents)}</Meta>
        )}
        {stats.goscore != null && (
          <Meta label="GoValue">{formatCurrency(stats.goscore)}</Meta>
        )}
        {stats.renewFeeCents != null && (
          <Meta label="Renew fee">{formatCurrencyCents(stats.renewFeeCents)}</Meta>
        )}
        {stats.ahrefsDr != null && <Meta label="Ahrefs DR">{stats.ahrefsDr}</Meta>}
        {stats.backlinks != null && (
          <Meta label="Backlinks">{stats.backlinks.toLocaleString()}</Meta>
        )}
        {stats.exactSearchVolume != null && (
          <Meta label="Exact search vol">{stats.exactSearchVolume.toLocaleString()}</Meta>
        )}
        {stats.alexaTrafficRank != null && (
          <Meta label="Alexa rank">{stats.alexaTrafficRank.toLocaleString()}</Meta>
        )}
        {stats.majesticTrustflow != null && (
          <Meta label="Majestic TF">{stats.majesticTrustflow}</Meta>
        )}
        {stats.semrushAscore != null && (
          <Meta label="SEMrush AS">{stats.semrushAscore}</Meta>
        )}
        {stats.extensionsTaken != null && (
          <Meta label="TLDs taken">{stats.extensionsTaken}</Meta>
        )}
        {stats.whoisCreateDate && (
          <Meta label="WHOIS created">
            {moment(stats.whoisCreateDate).utc().format('YYYY-MM-DD')}
          </Meta>
        )}
        {stats.parkingRevenueCents != null && (
          <Meta label="Parking rev">{formatCurrencyCents(stats.parkingRevenueCents)}</Meta>
        )}
        {stats.parkingUniques != null && (
          <Meta label="Parking uniques">{stats.parkingUniques.toLocaleString()}</Meta>
        )}
        {stats.parkingClicks != null && (
          <Meta label="Parking clicks">{stats.parkingClicks.toLocaleString()}</Meta>
        )}
      </MetaGrid>
    </Section>
  );
}

function AuditEventsSection({ events }: { events: DomainAuditEvent[] }) {
  const [page, setPage] = useState(1);
  const total = events.length;
  const lastPage = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));

  // Guard against the current page drifting past the end if events ever shrink.
  const safePage = Math.min(page, lastPage);
  const pageEvents = useMemo(() => {
    const start = (safePage - 1) * AUDIT_PAGE_SIZE;
    return events.slice(start, start + AUDIT_PAGE_SIZE);
  }, [events, safePage]);

  const firstIdx = total === 0 ? 0 : (safePage - 1) * AUDIT_PAGE_SIZE + 1;
  const lastIdx = Math.min(safePage * AUDIT_PAGE_SIZE, total);

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">
          Audit Events ({total})
        </div>
        {total > AUDIT_PAGE_SIZE && (
          <div className="text-[11px] font-mono text-text-3">
            {firstIdx}–{lastIdx} of {total}
          </div>
        )}
      </div>
      {total === 0 ? (
        <div className="text-text-3 text-[12.5px]">No events recorded.</div>
      ) : (
        <>
          <div className="border border-border rounded-md overflow-hidden">
            <DataTable<DomainAuditEvent>
              columns={auditColumns}
              data={pageEvents}
              rowKey={(e) => String(e.id)}
            />
          </div>
          {total > AUDIT_PAGE_SIZE && (
            <div className="flex justify-end mt-2 text-[11px] font-mono text-text-3">
              <Pagination
                page={safePage}
                pageSize={AUDIT_PAGE_SIZE}
                total={total}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

const auditColumns: Column<DomainAuditEvent>[] = [
  {
    key: 'createdAt',
    title: 'When',
    width: 160,
    render: (e) => (
      <span className="font-mono text-[11.5px] text-text-2 tabular-nums">
        {moment(e.createdAt).format('YYYY-MM-DD HH:mm')}
      </span>
    ),
  },
  {
    key: 'auditEventType',
    title: 'Event',
    render: (e) => (
      <span className="text-[12.5px] text-text font-medium">{e.auditEventType}</span>
    ),
  },
  {
    key: 'eventSource',
    title: 'Source',
    width: 140,
    render: (e) => (
      <span className="text-[11.5px] text-text-3 font-mono">{e.eventSource || '—'}</span>
    ),
  },
  {
    key: 'data',
    title: 'Data',
    render: (e) => <AuditDataCell data={e.data} />,
  },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-2">{title}</div>
      {children}
    </section>
  );
}

function MetaGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 bg-panel border border-border rounded-md px-4 py-3">
      {children}
    </div>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">{label}</span>
      <span className="text-[12.5px] text-text truncate">{children}</span>
    </div>
  );
}

function statusTone(status: string): TagTone {
  const s = status.toLowerCase();
  if (s.includes('active') || s.includes('ok')) return 'success';
  if (s.includes('hold') || s.includes('expired') || s.includes('locked')) return 'danger';
  if (s.includes('pending') || s.includes('grace')) return 'warn';
  return 'neutral';
}

function hasStats(stats: NonNullable<Domain['stats']>): boolean {
  return Object.values(stats).some((v) => v != null);
}
